
import { mdiFacebook, mdiInstagram, mdiYoutube } from "@mdi/js";
import { type PropType, computed, defineComponent, getCurrentInstance, onBeforeMount, ref } from "vue";

import useShadowModel from "../../composables/shadow-model";
import { CCol, CContainer, CDivider, CFooter, CIcon, CRow } from "../../vuetify-components";
import CAppStore from "../CAppStore/CAppStore.vue";
import CGooglePlay from "../CGooglePlay/CGooglePlay.vue";
import CLanguageSelect from "../CLanguageSelect/CLanguageSelect.vue";
import CLtkLogo from "../CLtkLogo/CLtkLogo.vue";

export default defineComponent({
  components: { CLanguageSelect, CGooglePlay, CAppStore, CFooter, CCol, CIcon, CRow, CDivider, CLtkLogo, CContainer },
  props: {
    currentLanguage: {
      type: String,
      default: "en_US",
    },
    availableLanguages: {
      type: Object as PropType<Record<string, string | undefined>>,
      default: () => ({}),
    },
    nuxt: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    "update:currentLanguage": (_v: string) => true,
    "open-store": (_v: string) => true,
    "logo-click": () => true,
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance()?.proxy;
    const height = ref("auto");
    const utmParams = ref("");

    const footer = ref<HTMLElement | undefined>();
    const selectedLanguage = useShadowModel("currentLanguage", props, emit);

    const linkComponent = computed(() => (props.nuxt === true ? "nuxt-link" : "router-link"));
    const copyrightYear = new Date().getFullYear();

    const showImpressumLink = computed(
      () => selectedLanguage.value.startsWith("de") || selectedLanguage.value.startsWith("en"),
    );

    const socialLinks = computed(() => [
      {
        href: "https://www.facebook.com/LIKEtoKNOWit-1543485825863332/",
        title: "facebook",
        icon: mdiFacebook,
      },
      {
        href: "https://www.youtube.com/channel/UCw_WZUDMPB0TDpWXtbowKKA/featured",
        title: "youtube",
        icon: mdiYoutube,
      },
      {
        href: "https://www.instagram.com/shop.ltk/?hl=en",
        title: "instagram",
        icon: mdiInstagram,
      },
    ]);

    const links = computed(() => [
      {
        heading: vm?.$gettext("Follow us"),
        links: [
          {
            href: `${vm?.$config.creatorSite}/more-to-say`,
            title: vm?.$gettext("Podcast: More to Say"),
          },
          {
            href: "https://www.instagram.com/shop.LTK/?hl=en",
            title: "@shop.LTK",
          },
          {
            href: "https://www.instagram.com/ltk.home/",
            title: "@LTK.home",
          },
          {
            href: "https://www.instagram.com/ltk.family/",
            title: "@LTK.family",
          },
          {
            href: "https://www.instagram.com/ltk.europe/",
            title: "@LTK.europe",
          },
          {
            href: "https://www.instagram.com/ltk.brasil/",
            title: "@LTK.brasil",
          },
          {
            href: "https://www.instagram.com/ltk.australia/",
            title: "@LTK.australia",
          },
          {
            href: "https://www.instagram.com/ltk.asia/",
            title: "@LTK.asia",
          },
        ],
      },
      {
        heading: vm?.$gettext("Learn more"),
        links: [
          {
            href: `/`,
            title: vm?.$gettext("LTK"),
          },
          {
            href: `${vm?.$config.creatorSite}/influencers/?${utmParams.value}`,
            title: vm?.$gettext("Become a Creator"),
          },
          {
            href: `${vm?.$config.creatorSite}/brands`,
            title: vm?.$gettext("Brand Platform"),
          },
          {
            href: `${vm?.$config.creatorSite}/press`,
            title: vm?.$gettext("Newsroom"),
          },
          {
            href: `${vm?.$config.creatorSite}/company`,
            title: vm?.$gettext("Company"),
          },
          {
            href: `${vm?.$config.creatorSite}/careers`,
            title: vm?.$gettext("Careers"),
          },
        ],
      },
      {
        heading: vm?.$gettext("Legal"),
        links: [
          {
            href: `${vm?.$config.creatorSite}/ltk-terms-of-service`,
            title: vm?.$gettext("Terms of Service"),
          },
          {
            href: `${vm?.$config.creatorSite}/ltk-privacy-policy`,
            title: vm?.$gettext("Privacy Policy"),
          },
          {
            href: `${vm?.$config.creatorSite}/ltk-cookie-policy`,
            title: vm?.$gettext("Cookie Policy"),
          },
          {
            href: `${vm?.$config.creatorSite}/imprint`,
            title: vm?.$gettext("Imprint"),
          },
          {
            href: `${vm?.$config.creatorSite}/ltk-do-not-sell`,
            title: vm?.$gettext("Do Not Sell"),
          },
          {
            to: "/legal/virtual-patent",
            title: vm?.$gettext("Patents"),
          },
        ],
      },
      {
        heading: vm?.$gettext("Support"),
        links: [
          {
            href: "https://help.liketoknow.it/",
            title: vm?.$gettext("Help Center"),
          },
          {
            href: "/sitemap",
            title: vm?.$gettext("Site Map"),
          },
          {
            href: "mailto:info@liketoknow.it",
            title: vm?.$gettext("Contact Us"),
          },
        ],
      },
    ]);

    /**
     * Set the height of the footer manually. When it changes from a flex row to column, this
     * increases the height of the footer, but vuetify is only setup to grab the footer's
     * height on initial render unless specified in a prop. This will solve some full screen
     * issues concerning resizing the window and the main content not resizing to full screen.
     */
    const onResize = () => {
      height.value = `${footer.value?.offsetHeight}px`;
    };

    onBeforeMount(() => {
      const currentParams = new URLSearchParams(window.location?.search);
      const filteredParams = new URLSearchParams();

      currentParams.forEach((value, key) => {
        if (key.startsWith("utm_")) {
          filteredParams.append(key, value);
        }
      });

      utmParams.value = filteredParams.toString();
    });

    return {
      height,
      utmParams,
      footer,
      selectedLanguage,
      links,
      linkComponent,
      copyrightYear,
      showImpressumLink,
      socialLinks,
      onResize,
    };
  },
});
